/**
 * @fileoverview Syndicator Showdown — Share tracking service (Phase 6)
 * @module services/shareTrackingService
 *
 * Thin, fire-and-forget wrapper around the trackShareEvent callable, plus
 * local storage of "share attribution" (which shareId/owner a logged-out
 * visitor clicked a CTA from) so a subsequent sign-up can be connected back
 * to the share that drove it — mirroring referralService's existing
 * store/resolve pattern for referral codes.
 *
 * Every export here is best-effort: a tracking failure must never block or
 * visibly error for the player.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const ATTRIBUTION_KEY = 'ss_share_attribution';
const VIEWED_SHARES_KEY = 'ss_viewed_shares';

let cachedCallable = null;
function getTrackShareEventCallable() {
  if (!cachedCallable) {
    cachedCallable = httpsCallable(functions, 'trackShareEvent');
  }
  return cachedCallable;
}

/**
 * Records a share event against a stable_shares/{shareId} document. Never
 * throws — logs and swallows any failure.
 *
 * @param {string} shareId
 * @param {'view'|'copy'|'native_share'|'qr_open'|'challenge_click'} eventType
 * @param {{ unique?: boolean }} [opts]
 */
export async function trackShareEvent(shareId, eventType, opts = {}) {
  if (!shareId || !eventType) return;
  try {
    await getTrackShareEventCallable()({ shareId, eventType, unique: !!opts.unique });
  } catch (err) {
    console.warn('[shareTrackingService] trackShareEvent failed (non-blocking):', err?.message);
  }
}

/**
 * Records a public-page view, automatically working out whether this is the
 * first time THIS browser session has viewed this specific share link (a
 * simple, privacy-safe "unique view" estimate — no fingerprinting).
 *
 * @param {string} shareId
 */
export function trackShareView(shareId) {
  if (!shareId) return;
  let unique = false;
  try {
    const seen = JSON.parse(sessionStorage.getItem(VIEWED_SHARES_KEY) || '[]');
    if (!seen.includes(shareId)) {
      unique = true;
      sessionStorage.setItem(VIEWED_SHARES_KEY, JSON.stringify([...seen, shareId].slice(-50)));
    }
  } catch {
    // Non-fatal — worst case every view in this session is counted as unique.
    unique = true;
  }
  trackShareEvent(shareId, 'view', { unique });
}

// ─── Share attribution (challenge-a-friend / viral loop) ──────────────────────

/**
 * Persists which share a logged-out visitor clicked a CTA from, so sign-up
 * can attribute the new account back to the shareId (in addition to any
 * referral code attribution already in place).
 *
 * @param {{ shareId: string, ownerUid: string }} attribution
 */
export function storeShareAttribution({ shareId, ownerUid }) {
  if (!shareId) return;
  try {
    const payload = JSON.stringify({ shareId, ownerUid: ownerUid || null });
    localStorage.setItem(ATTRIBUTION_KEY, payload);
    sessionStorage.setItem(ATTRIBUTION_KEY, payload);
  } catch (err) {
    console.warn('[shareTrackingService] storeShareAttribution failed:', err?.message);
  }
}

/** @returns {{ shareId: string, ownerUid: string|null }|null} */
export function getStoredShareAttribution() {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY) || sessionStorage.getItem(ATTRIBUTION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearStoredShareAttribution() {
  try {
    localStorage.removeItem(ATTRIBUTION_KEY);
    sessionStorage.removeItem(ATTRIBUTION_KEY);
  } catch {
    // Non-fatal
  }
}

/**
 * Best-effort — called once at sign-up time (mirrors referralService's
 * recordReferralSignup) to bump signupsAttributed on the originating share,
 * connecting new-account attribution back to a specific share link where
 * practical. Never throws.
 *
 * @param {string} shareId
 */
export async function recordShareSignupAttribution(shareId) {
  if (!shareId) return;
  await trackShareEvent(shareId, 'signup');
}
